import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { SubscriptionService } from "../services/subscription.service";
import SettingsSidebar from "../../../partials/settings/SettingsSidebar";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import { theme } from "@shared/utils/themeColors";

/**
 * Page for confirming subscription downgrade to free plan
 */
const SubscriptionDowngradeToFreeConfirmPage: React.FC = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);

  // Downgrade to free plan mutation
  const { mutate: downgradeToFree } = useMutation({
    mutationFn: (subId: string) => SubscriptionService.downgradeToFree(subId),
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message || "Successfully downgraded to free plan");
        queryClient.invalidateQueries({ queryKey: ["currentSubscription"] });
        navigate("/settings/subscription-change/downgrade-free/success");
      } else {
        toast.error(data.message || "Failed to downgrade to free plan");
        setIsProcessing(false);
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Error downgrading to free plan");
      setIsProcessing(false);
    },
  });

  const handleConfirmDowngrade = () => {
    if (!subscriptionId) {
      toast.error("Subscription ID is missing");
      return;
    }

    setIsProcessing(true);
    downgradeToFree(subscriptionId);
  };

  const handleGoBack = () => {
    navigate("/settings/plans");
  };

  return (
    <AuthenticatedLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        {/* Page header */}
        <div className="mb-8">
          {/* Title */}
          <h1 className={`text-2xl md:text-3xl ${theme.text.primary} font-bold`}>
            Account Settings
          </h1>
        </div>

        {/* Content */}
        <div className={`${theme.bg.primary} shadow-sm rounded-xl mb-8`}>
          <div className="flex flex-col md:flex-row md:-mr-px">
            <SettingsSidebar />
            <div className="grow">
              <div className="p-6">
                <div className="max-w-md mx-auto">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                    <svg
                      className="w-8 h-8 text-yellow-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>

                  <h2 className={`text-2xl font-bold text-center ${theme.text.primary} mb-4`}>
                    Downgrade to Free Plan
                  </h2>

                  <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
                    Are you sure you want to downgrade to the free plan? Your premium features will remain active until the end of your current billing period. After that, your account will automatically switch to the free plan.
                  </p>

                  <div className="space-y-4">
                    <button
                      onClick={handleConfirmDowngrade}
                      disabled={isProcessing}
                      className={`w-full py-2 px-4 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 transition-colors ${
                        isProcessing ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                    >
                      {isProcessing ? "Processing..." : "Yes, Downgrade to Free Plan"}
                    </button>

                    <button
                      onClick={handleGoBack}
                      disabled={isProcessing}
                      className={`w-full py-2 px-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 transition-colors ${
                        isProcessing ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                    >
                      No, Keep My Current Plan
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default SubscriptionDowngradeToFreeConfirmPage;
